import { Mutex } from 'async-mutex';
import { jwtVerify, importJWK, JWTPayload } from 'jose';
import { DateTime } from 'luxon';
import PortalServiceClient from '../api/PortalServiceClient';
import { localLog } from '../loggers/Logger';
import AuthenticationInfo from './AuthenticationInfo';
import EnvironmentConfig from './EnvironmentConfig';

class AuthenticationManager {
  public static create() {
    if (!this.instance) {
      this.instance = new AuthenticationManager();
    }
    return this.instance;
  }
  private static instance: AuthenticationManager;
  private portalService: PortalServiceClient;
  // @ts-expect-error TS2564 - strict migration
  private currentToken: string;
  // @ts-expect-error TS2564 - strict migration
  private onSuccess: (info: AuthenticationInfo) => void;
  // @ts-expect-error TS2564 - strict migration
  private onError: (err: Error) => void;
  private mutex = new Mutex();
  // @ts-expect-error TS2564 - strict migration
  private authnInfo: AuthenticationInfo;

  get authInfo() {
    return this.authnInfo;
  }

  constructor() {
    this.portalService = PortalServiceClient.create();
  }

  public configure(
    onSuccess: (info: AuthenticationInfo) => Promise<void>,
    onError: (err: Error) => Promise<void>,
  ) {
    this.onSuccess = onSuccess;
    this.onError = onError;
  }

  public async renewAuthorInfo() {
    const release = await this.mutex.acquire();
    let authnInfo;
    try {
      authnInfo = await this.verifyToken(this.currentToken);
    } catch (e) {
      authnInfo = await this.refreshToken();
    } finally {
      release();
    }
    // @ts-expect-error TS2322 - strict migration
    this.authnInfo = authnInfo;
    return authnInfo;
  }

  // @ts-expect-error TS7030 - strict migration
  public async refreshToken() {
    try {
      localLog.debug('Refreshing token...');
      const token = await this.portalService.getAuthenticationToken();
      this.currentToken = token;
      const authorInfo = await this.processToken(token);
      localLog.debug('Token refresh completed');
      return authorInfo;
    } catch (err) {
      // @ts-expect-error TS18046 - strict migration
      localLog.debug(`Failed to refresh token: ${JSON.stringify(err)} ${err.message}`);
      if (this.onError) {
        // @ts-expect-error TS2345 - strict migration
        await this.onError(err);
      }
    }
  }

  // @ts-expect-error TS7030 - strict migration
  public async processToken(token: string) {
    try {
      const authInfo = await this.verifyToken(token);
      this.authnInfo = authInfo;
      if (this.onSuccess) {
        await this.onSuccess(authInfo);
      }
      return authInfo;
    } catch (err) {
      // @ts-expect-error TS18046 - strict migration
      localLog.debug(`Failed to process token: ${JSON.stringify(err)} ${err.message}`);
      if (this.onError) {
        // @ts-expect-error TS2345 - strict migration
        await this.onError(err);
      }
    }
  }

  private async verifyToken(token: string) {
    try {
      const jwks = await this.portalService.getJWKS();
      let payload: JWTPayload;

      for (const key of jwks.keys) {
        const publicKey = await importJWK(key);
        const verifyResult = await jwtVerify(token, publicKey, {
          issuer: EnvironmentConfig.getConfig().tokenIssuer,
        });

        payload = verifyResult.payload;
        break;
      }

      const userProfile = await this.portalService.getSignedInUserProfile();

      const authInfo: AuthenticationInfo = {
        tokenInfo: {
          // @ts-expect-error TS2454 - strict migration
          expiryDate: DateTime.fromMillis(payload.exp),
          token,
        },
        userProfile,
      };

      return authInfo;
    } catch (err) {
      localLog.error({ err }, 'Error while refreshing token.');
      throw err;
    }
  }
}

export default AuthenticationManager;
